import { textParts } from './links'
import { notionPage, type NotionPage } from './notion'

// The description box is an editable element rather than a textarea, so that a Notion link can sit in
// it as a chip. What is saved stays the plain text: a chip stands for its address, kept in data-url.
// Other web addresses and phone numbers stay text you can edit, in a span whose data-href says where
// they go. These helpers turn the text into the box's contents and back, and keep the caret where it was.

/** After a chip that ends a line, so the caret has somewhere to stand; never part of the text. */
const SPACER = '​'
/** The <br> that lets a final empty line show; never part of the text. */
const END = 'end'

/** Fills `box` with `text`, every Notion page in it drawn by `chip` and every other link marked. */
export function draw(box: HTMLElement, text: string, chip: (page: NotionPage) => HTMLElement): void {
  const doc = box.ownerDocument
  const nodes: Node[] = []
  for (const part of textParts(text)) {
    if (!('href' in part)) {
      nodes.push(doc.createTextNode(part.text))
      continue
    }
    const page = notionPage(part.href)
    if (!page) {
      const link = doc.createElement('span')
      link.dataset.href = part.href
      link.textContent = part.text
      nodes.push(link)
      continue
    }
    const el = chip(page)
    el.dataset.url = part.text
    el.contentEditable = 'false'
    nodes.push(el)
  }
  // a spacer wherever a chip has no text after it on its line
  const spaced: Node[] = []
  nodes.forEach((node, i) => {
    spaced.push(node)
    const next = nodes[i + 1]
    if (isChip(node) && (!next || isChip(next) || next.textContent?.startsWith('\n'))) spaced.push(doc.createTextNode(SPACER))
  })
  if (text.endsWith('\n')) {
    const br = doc.createElement('br')
    br.dataset.role = END
    spaced.push(br)
  }
  box.replaceChildren(...spaced)
}

/**
 * The plain text in `node`, up to the point `upTo` if given. Reads whatever the browser has made of
 * it as well as what `draw` made: text, chips, line breaks and the blocks some browsers wrap lines in.
 */
export function read(node: Node, upTo?: { node: Node; offset: number }): string {
  let out = ''
  let done = false
  const walk = (parent: Node) => {
    const kids = parent.childNodes
    for (let i = 0; i < kids.length && !done; i++) {
      if (upTo && parent === upTo.node && i === upTo.offset) {
        done = true
        return
      }
      const kid = kids[i]
      if (kid.nodeType === 3) {
        const t = kid.textContent ?? ''
        out += (upTo && kid === upTo.node ? t.slice(0, upTo.offset) : t).replaceAll(SPACER, '')
        if (upTo && kid === upTo.node) done = true
      } else if (kid.nodeType === 1) {
        const el = kid as HTMLElement
        if (el.dataset.url) out += el.dataset.url
        // the last <br> of a line's block, or of the box, only holds that line open; it is no line of its own
        else if (el.tagName === 'BR') out += el.dataset.role === END || !el.nextSibling ? '' : '\n'
        else {
          const block = el.tagName === 'DIV' || el.tagName === 'P'
          if (block && out !== '' && !out.endsWith('\n')) out += '\n'
          walk(el)
        }
      }
    }
  }
  walk(node)
  // a browser that keeps line breaks as text holds a final empty line open with one more "\n" of its own
  const last = node.lastChild
  if (!upTo && last?.nodeType === 3 && last.textContent?.endsWith('\n') && out.endsWith('\n')) out = out.slice(0, -1)
  return out
}

/** Whether the box shows its text as `draw` would, or typing has left a link unmarked or a mark on what is no link. */
export function isDrawn(box: HTMLElement, chip: (page: NotionPage) => HTMLElement): boolean {
  const fresh = box.ownerDocument.createElement('div')
  draw(fresh, read(box), chip)
  return fresh.innerHTML === box.innerHTML
}

/** Whether the box holds a Notion page that is still written out rather than drawn as a chip. */
export function hasBareNotionLink(box: HTMLElement): boolean {
  // chips as spaces, so text either side of one never joins up into an address
  const text = [...box.childNodes].map((n) => (isChip(n) ? ' ' : read(wrap(n)))).join('')
  return textParts(text).some((p) => 'href' in p && notionPage(p.href) !== null)
}

/** Where the caret is, counted in characters of the plain text; null when it is not in the box. */
export function caretOffset(box: HTMLElement): number | null {
  const sel = box.ownerDocument.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const r = sel.getRangeAt(0)
  if (!box.contains(r.endContainer)) return null
  return read(box, { node: r.endContainer, offset: r.endOffset }).length
}

/** Puts the caret `offset` characters into the box as `draw` left it; inside a chip counts as after it. */
export function placeCaret(box: HTMLElement, offset: number): void {
  const doc = box.ownerDocument
  const sel = doc.getSelection()
  if (!sel) return
  let left = offset
  const kids = [...box.childNodes]
  let at: { node: Node; offset: number } = { node: box, offset: kids.length }
  for (let i = 0; i < kids.length; i++) {
    const kid = kids[i]
    if (kid.nodeType === 3) {
      const t = kid.textContent ?? ''
      if (t === SPACER) continue
      if (left <= t.length) {
        at = { node: kid, offset: left }
        break
      }
      left -= t.length
    } else if (isChip(kid)) {
      if (left === 0) {
        at = { node: box, offset: i }
        break
      }
      const len = (kid as HTMLElement).dataset.url!.length
      if (left <= len) {
        // after the chip; in its spacer if it has one, so typing lands on the chip's line
        const next = kids[i + 1]
        at = next?.textContent === SPACER ? { node: next, offset: 1 } : { node: box, offset: i + 1 }
        break
      }
      left -= len
    } else if (isLink(kid)) {
      // at a link's end the caret goes on into what follows, so typing there is not part of the link
      const t = kid.firstChild
      const len = t?.textContent?.length ?? 0
      if (t && left < len) {
        at = { node: t, offset: left }
        break
      }
      left -= len
    } else if (kid.nodeName === 'BR' && (kid as HTMLElement).dataset.role === END) {
      at = { node: box, offset: i }
      break
    }
  }
  const range = doc.createRange()
  range.setStart(at.node, at.offset)
  range.collapse(true)
  sel.removeAllRanges()
  sel.addRange(range)
}

/** The chip right beside a collapsed caret, on the side a Backspace (`before`) or Delete would take. */
export function chipBeside(box: HTMLElement, before: boolean): HTMLElement | null {
  const sel = box.ownerDocument.getSelection()
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null
  const { startContainer: node, startOffset: offset } = sel.getRangeAt(0)
  if (!box.contains(node)) return null
  let neighbour: Node | null = null
  if (node.nodeType === 3) {
    const t = node.textContent ?? ''
    // a spacer is invisible, so the caret on either side of it is beside the chip
    const rest = before ? t.slice(0, offset).replaceAll(SPACER, '') : t.slice(offset).replaceAll(SPACER, '')
    if (rest === '') neighbour = before ? node.previousSibling : node.nextSibling
    // at the edge of a link, what is beside the link
    const parent = node.parentNode
    if (rest === '' && !neighbour && parent && parent !== box && isLink(parent)) neighbour = before ? parent.previousSibling : parent.nextSibling
  } else {
    neighbour = node.childNodes[before ? offset - 1 : offset] ?? null
    if (neighbour?.textContent === SPACER) neighbour = before ? neighbour.previousSibling : neighbour.nextSibling
  }
  return neighbour && isChip(neighbour) ? (neighbour as HTMLElement) : null
}

function isChip(node: Node): boolean {
  return node.nodeType === 1 && !!(node as HTMLElement).dataset.url
}

function isLink(node: Node): boolean {
  return node.nodeType === 1 && !!(node as HTMLElement).dataset.href
}

function wrap(node: Node): Node {
  const holder = node.ownerDocument!.createElement('span')
  holder.append(node.cloneNode(true))
  return holder
}
